import { Combobox as BaseCombobox } from '@base-ui/react';
import { Lock as LockIcon } from 'components/icons';
import AutocompleteTag from 'components/AutocompleteTag';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
    ComboboxTrigger,
    useComboboxAnchor,
} from 'components/ui/combobox';
import { InputGroupButton } from 'components/ui/input-group';
import get from 'lodash/get';
import React, { useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { groupConsecutiveOptions, OptionGroup, useAutocompleteGroupingV2 } from 'utils/autocompleteGrouping';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { useValidClassifierOptions } from 'utils/useClassifiers.ts';
import ReadonlyField from '../rhf-other/ReadonlyField';
import {
    SECTION_CHIPS_INPUT_CLASS,
    SECTION_MULTI_SELECT_CLASS,
    type GetTagProps,
    type MultiSelectChangeDetails,
    type MultiSelectChangeReason,
} from './DynamicMultiSelectInput';
import { CommonInputProps, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;

type SelectAllOption = { __selectAll: true; label: string; value: '__select_all' };

const isSelectAllOption = (o: unknown): o is SelectAllOption =>
    typeof o === 'object' && o !== null && (o as { __selectAll?: unknown }).__selectAll === true;

interface Props<TFormData extends FieldValues, TOption extends FieldValues> extends CommonInputProps<TFormData> {
    options: TOption[];
    placeholderOnFocus?: string;
    noPreSelect?: boolean;
    clearable?: boolean;
    onChange?: (
        value: TOption[] | null,
        reason?: MultiSelectChangeReason,
        details?: MultiSelectChangeDetails<TOption> | undefined
    ) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    setValue: SetFieldValue<TFormData>;
    control: Control<TFormData>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    sortDirection?: 'asc' | 'desc';
    groupSortOrder?: string[];
    defaultGroup?: string;
    noOptionsContent?: React.ReactNode;
    readonly?: boolean | { linkTo: string };
    hasSelectAllOption?: boolean;
    /** Function to determine if an option is fixed (cannot be removed). Fixed options display with a lock icon. */
    getOptionFixed?: (option: TOption) => boolean;
}

const MultiSelectInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    label,
    shouldTranslateLabel = true,
    required,
    tooltip,
    rightLabelComponent,
    disabled,
    placeholder,
    shouldTranslatePlaceholder = true,
    validate,
    placeholderOnFocus,
    wrapperClassName,
    control,
    options,
    noPreSelect,
    clearable,
    onChange,
    getOptionLabel,
    setValue,
    getOptionIdentifier,
    sortDirection,
    groupSortOrder,
    defaultGroup,
    noOptionsContent,
    readonly = false,
    hasSelectAllOption = false,
    getOptionFixed,
}: Props<TFormData, TOption>) => {
    const watchedValue = useWatch({ control, name });
    // A record section in edit draws the values as its read view does: the whole row for stored values and one
    // cell without, in chips whose remove button takes no room, so a chip is as wide in both.
    const inSection = useInRecordSection();
    const wideClass = useSectionWideClass(control, name);

    const validOptions = useValidClassifierOptions(options, watchedValue);

    const rules = useMemoizedRules({ required, validate, requiredMessageId: 'validation.choose-option' });
    const isEmpty =
        watchedValue === undefined ||
        watchedValue === null ||
        (Array.isArray(watchedValue) && watchedValue.length === 0);
    const initialPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder) ?? t('select');
    const [controlledPlaceholder, setControlledPlaceholder] = useState<string>(initialPlaceholder);
    const shouldPreSelectFirstValue = required && validOptions.length === 1 && !noPreSelect;
    const firstValue = shouldPreSelectFirstValue ? validOptions.at(0) : watchedValue;
    const fallbackTranslation = defaultGroup ?? t('field.other');

    const selectAllOption = hasSelectAllOption
        ? ({ __selectAll: true, label: t('forms.select-all'), value: '__select_all' } as unknown as TOption)
        : null;
    const optionsWithSelectAll =
        hasSelectAllOption && selectAllOption ? [selectAllOption, ...validOptions] : validOptions;

    const { hasGroupedOptions, optionsMaybeSorted } = useAutocompleteGroupingV2(optionsWithSelectAll, groupSortOrder);
    // ponytail: LAST_NAME_FIRST flips options carrying first+last (person/employee) to "Last First".
    const lastNameFirst = useLastNameFirst();

    const getLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (isSelectAllOption(option)) {
            return t('forms.select-all');
        }

        if (typeof getOptionLabel === 'string') {
            return optionDisplayLabel(option, lastNameFirst, get(option, getOptionLabel));
        } else {
            return getOptionLabel(option);
        }
    };

    const getValue = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (isSelectAllOption(option)) {
            return '__select_all';
        }

        if (typeof getOptionIdentifier === 'string') {
            return get(option, getOptionIdentifier);
        } else {
            return getOptionIdentifier(option);
        }
    };

    const getSortedOptions = (options: TOption[], direction: 'asc' | 'desc' | undefined) => {
        if (direction === undefined) {
            return options;
        }

        const selectAllItems = options.filter((option) => isSelectAllOption(option));
        const regularOptions = options.filter((option) => !isSelectAllOption(option));

        const sortedRegularOptions = regularOptions.toSorted((a, b) => {
            if (direction === 'asc') {
                return String(getLabel(a)).localeCompare(String(getLabel(b)));
            } else {
                return String(getLabel(b)).localeCompare(String(getLabel(a)));
            }
        });

        return [...selectAllItems, ...sortedRegularOptions];
    };

    // TODO(react-doctor: no-prop-callback-in-effect): `onChange` is the consumer's
    // RHF Controller-style callback and `setValue` is RHF's setter — both are
    // framework APIs for syncing form state when a single option auto-selects.
    // Moving this into a render-phase computation would loop. Intentionally kept.
    // preselects the first value if the field is required, has only ONE option & doesn't have "noPreSelect: true" set
    useEffect(() => {
        if (!watchedValue && shouldPreSelectFirstValue && firstValue) {
            if (onChange) {
                onChange([firstValue]);
            }
            setValue(name, [firstValue]);
        }
    }, [name, onChange, setValue, watchedValue, shouldPreSelectFirstValue, firstValue]);

    const [open, setOpen] = useState(false);
    const chipsAnchorRef = useComboboxAnchor();
    // Base UI keeps filtering by the text typed before a pick until the list has finished closing. Typing the
    // next name before that reopens the list with that text still held, and nothing matches it: its option has
    // just left the list. So an open list is matched against what the input holds.
    // shortcut: a held text that is empty filters nothing and is not replaced, filter the options here if that shows.
    const [typed, setTyped] = useState('');
    const { contains } = BaseCombobox.useFilter({ multiple: true });

    const allOptions = hasGroupedOptions ? optionsMaybeSorted : getSortedOptions(optionsWithSelectAll, sortDirection);

    // Already-picked options are taken out of the list rather than shown ticked: the chips above the
    // input are what says they are selected.
    const selectableOptions = useMemo(() => {
        const selectedIds = new Set(((watchedValue ?? []) as TOption[]).map((option) => getValue(option)));
        return allOptions.filter((option) => !selectedIds.has(getValue(option)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allOptions, watchedValue, getOptionIdentifier]);

    const groupedOptions = useMemo<OptionGroup<TOption>[]>(() => {
        if (!hasGroupedOptions) {
            return [];
        }
        return groupConsecutiveOptions(selectableOptions, (option) => String(option.group ?? fallbackTranslation));
    }, [hasGroupedOptions, selectableOptions, fallbackTranslation]);

    const getSameNameEmail = useSameNameEmail(allOptions);

    const renderOptionRow = (option: TOption) => (
        <ComboboxItem key={getValue(option)} value={option} title={getLabel(option) || undefined}>
            <PersonOptionLabel option={option} label={getLabel(option)} sameNameEmail={getSameNameEmail(option)} />
        </ComboboxItem>
    );

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { ref, onChange: innerOnChange, onBlur, value }, fieldState: { error } }) => {
                const selected = (value ?? []) as TOption[];

                const commit = (
                    next: TOption[],
                    reason: MultiSelectChangeReason,
                    details?: MultiSelectChangeDetails<TOption>
                ) => {
                    innerOnChange(next);
                    if (onChange) {
                        onChange(next, reason, details);
                    }
                };

                const getTagProps: GetTagProps = ({ index }) => ({
                    key: getValue(selected[index]) ?? index,
                    disabled,
                    onDelete: () =>
                        commit(
                            selected.filter((_, position) => position !== index),
                            'removeOption',
                            { option: selected[index] }
                        ),
                });

                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        shouldTranslateLabel={shouldTranslateLabel}
                        required={required}
                        isEmpty={isEmpty}
                        tooltip={tooltip}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        className={cn(wrapperClassName, inSection && SECTION_MULTI_SELECT_CLASS, wideClass)}
                    >
                        {readonly ? (
                            <ReadonlyField
                                value={value}
                                getLabel={getLabel}
                                getValue={getValue}
                                linkTo={typeof readonly === 'object' ? readonly?.linkTo : undefined}
                            />
                        ) : (
                            <Combobox<TOption, true>
                                multiple
                                items={(hasGroupedOptions ? groupedOptions : selectableOptions) as readonly TOption[]}
                                value={selected}
                                disabled={disabled}
                                itemToStringLabel={(option) => String(getLabel(option) ?? '')}
                                isItemEqualToValue={(option, selectedOption) =>
                                    getValue(option) === getValue(selectedOption)
                                }
                                open={open}
                                onOpenChange={setOpen}
                                onInputValueChange={setTyped}
                                filter={(option, query, itemToString) =>
                                    contains(option, open ? typed.trim() : query, itemToString)
                                }
                                onValueChange={(next, details) => {
                                    const isRemoval = next.length < selected.length;
                                    let finalData = next;

                                    // "Select all" is a row, not a value: picking it swaps in every
                                    // option and closes the list, unpicking it empties the field down
                                    // to whatever is fixed.
                                    if (hasSelectAllOption) {
                                        const hasSelectAll = next.some((option) => isSelectAllOption(option));
                                        if (hasSelectAll) {
                                            finalData = validOptions;
                                            setOpen(false);
                                        }
                                    }

                                    // The option that moved is found by diffing the two lists, never
                                    // by position: a chip's own Backspace/Delete takes the value out at
                                    // that chip's index, so neither list's last entry names it.
                                    const removed = selected.filter(
                                        (option) => !next.some((remaining) => getValue(remaining) === getValue(option))
                                    );
                                    const added = next.filter(
                                        (option) =>
                                            !selected.some((previous) => getValue(previous) === getValue(option))
                                    );

                                    if (isRemoval && getOptionFixed && removed.some((o) => getOptionFixed(o))) {
                                        // Fixed values cannot be taken out; put them back.
                                        finalData = [...finalData, ...removed.filter((o) => getOptionFixed(o))];
                                    }

                                    const reason = isRemoval
                                        ? details.reason === 'clear-press'
                                            ? 'clear'
                                            : 'removeOption'
                                        : 'selectOption';
                                    commit(finalData, reason, {
                                        option: (isRemoval ? removed[0] : added[0]) as TOption,
                                    });
                                }}
                            >
                                {/* The chips row is the anchor, so the popup lines up with the whole
                                    field rather than with the text caret inside it. Its `min-h-9` is
                                    what keeps an empty field and a one-chip field the same height. */}
                                <ComboboxChips
                                    ref={chipsAnchorRef}
                                    // The kit tightens this padding once a chip is present; holding it
                                    // steady keeps the caret from stepping sideways on the first pick.
                                    className="bg-paper has-data-[slot=combobox-chip]:px-2.5"
                                >
                                    {selected.map((option, index) => {
                                        const isFixed = getOptionFixed ? getOptionFixed(option) : false;
                                        return (
                                            // The chip is what registers into the composite list the
                                            // input's arrow keys walk; its own pill styling is dropped
                                            // because the tag inside already draws one.
                                            <ComboboxChip
                                                key={getValue(option) ?? index}
                                                showRemove={false}
                                                className="h-auto max-w-full bg-transparent p-0"
                                            >
                                                <AutocompleteTag
                                                    section={inSection}
                                                    label={getLabel(option)}
                                                    title={getLabel(option) || undefined}
                                                    icon={isFixed ? <LockIcon fontSize="small" /> : undefined}
                                                    disabled={disabled}
                                                    onDelete={isFixed ? undefined : getTagProps({ index }).onDelete}
                                                />
                                            </ComboboxChip>
                                        );
                                    })}
                                    <ComboboxChipsInput
                                        id={name}
                                        name={name}
                                        ref={ref}
                                        className={inSection ? SECTION_CHIPS_INPUT_CLASS : undefined}
                                        aria-invalid={!!error}
                                        required={required ?? false}
                                        disabled={disabled}
                                        placeholder={controlledPlaceholder}
                                        data-form-type="other"
                                        data-lpignore="true"
                                        onFocus={() => {
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(t(placeholderOnFocus));
                                            }
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(initialPlaceholder);
                                            }
                                        }}
                                    />
                                    {/* Only mounts once something is selected. Pre-selecting the sole
                                        required option is the one case where emptying the field is not
                                        offered, as it was before. Sized to the input's own line so it
                                        never out-grows the row's `min-h-9`: the kit's 24px button would
                                        add 2px on the first pick. */}
                                    {clearable !== false && !shouldPreSelectFirstValue ? (
                                        <ComboboxClear className="size-5" />
                                    ) : null}
                                    {/* The chevron every dropdown shows, at the clear button's size and,
                                        like the single-value fields', with no Tab stop. */}
                                    <InputGroupButton
                                        size="icon-xs"
                                        variant="ghost"
                                        render={<ComboboxTrigger />}
                                        tabIndex={-1}
                                        className="-mr-1 size-5 data-pressed:bg-transparent"
                                        disabled={disabled}
                                    />
                                </ComboboxChips>
                                <ComboboxContent anchor={chipsAnchorRef} className="shadow-dropdown">
                                    <ComboboxEmpty>{noOptionsContent ?? t('no-options')}</ComboboxEmpty>
                                    <ComboboxList>
                                        {hasGroupedOptions
                                            ? (group: OptionGroup<TOption>) => (
                                                  <ComboboxGroup key={group.key} items={group.items}>
                                                      <ComboboxLabel>{group.value}</ComboboxLabel>
                                                      <ComboboxCollection>
                                                          {(option: TOption) => renderOptionRow(option)}
                                                      </ComboboxCollection>
                                                  </ComboboxGroup>
                                              )
                                            : (option: TOption) => renderOptionRow(option)}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                        )}
                    </InputWrapper>
                );
            }}
        />
    );
};

export default MultiSelectInput;
